import { useCallback, useState } from "react";
import {
  ActionIcon,
  Box,
  Container,
  Drawer,
  Flex,
  Group,
  Stack,
  Text,
  rem,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconGift } from "@tabler/icons-react";
import CustomerOfferHighlightBannerForm, {
  CustomerOfferHighlightSaveButton,
  type CustomerOfferBannerSaveState,
} from "./CustomerOfferHighlightBannerForm";

export const BANNER_TITLE = "New: Highlight what you offer customers";

const BANNER_DESCRIPTION =
  "Add an optional line to your company profile for trials, starter credits, included services, or other perks—so partners discover what you give (e.g. 15-day trial, 50 tokens to start).";

const BANNER_SHELL_STYLES = {
  borderBottom: "1px solid rgba(4, 48, 92, 0.12)",
  background: "linear-gradient(98deg, #fff8e1 0%, #ffecb3 100%)",
} as const;

const BANNER_CARD_STYLES = {
  backgroundColor: "transparent",
  border: "1px solid rgba(180, 83, 9, 0.35)",
  borderRadius: 8,
} as const;

type CustomerOfferHighlightBannerProps = {
  companyId: string;
  onSaved: () => void | Promise<void>;
};

export default function CustomerOfferHighlightBanner({
  companyId,
  onSaved,
}: CustomerOfferHighlightBannerProps) {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [saveState, setSaveState] = useState<CustomerOfferBannerSaveState>({
    canSave: false,
    saving: false,
  });

  const handleSaveStateChange = useCallback((state: CustomerOfferBannerSaveState) => {
    setSaveState(state);
  }, []);

  const handleSaved = async () => {
    await onSaved();
    closeDrawer();
  };

  const form = (
    <CustomerOfferHighlightBannerForm
      companyId={companyId}
      onSaved={handleSaved}
      showSaveButton={false}
      onSaveStateChange={handleSaveStateChange}
    />
  );

  const description = (
    <Text size="sm" c="#422006">
      {BANNER_DESCRIPTION}
    </Text>
  );

  const titleRow = (
    <Flex justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
      <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0, flex: 1 }}>
        <IconGift size={22} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
        <Text fw={700} size="sm" c="#422006" lh={1.35} style={{ minWidth: 0 }}>
          {BANNER_TITLE}
        </Text>
      </Group>
      <Box style={{ flexShrink: 0 }}>
        <CustomerOfferHighlightSaveButton {...saveState} />
      </Box>
    </Flex>
  );

  return (
    <Box
      component="aside"
      px={{ base: rem(12), sm: rem(20) }}
      py={rem(10)}
      style={BANNER_SHELL_STYLES}
    >
      <Container size={1414} px={0}>
        {/* Mobile: compact heading + open drawer */}
        <Box hiddenFrom="sm">
          <Group
            justify="space-between"
            align="center"
            wrap="nowrap"
            gap="xs"
            p="sm"
            style={BANNER_CARD_STYLES}
          >
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
              <IconGift size={22} color="#b45309" style={{ flexShrink: 0 }} />
              <Text fw={700} size="sm" c="#422006" lh={1.35} style={{ minWidth: 0 }}>
                {BANNER_TITLE}
              </Text>
            </Group>
            <ActionIcon
              variant="subtle"
              color="#b45309"
              size="lg"
              radius="md"
              aria-label="Open offer picker"
              onClick={openDrawer}
              style={{ flexShrink: 0 }}
            >
              <IconChevronDown size={20} />
            </ActionIcon>
          </Group>

          <Drawer
            opened={drawerOpened}
            onClose={closeDrawer}
            position="bottom"
            size="auto"
            withCloseButton={false}
            padding="md"
            zIndex={400}
            styles={{
              header: { display: "none" },
              content: {
                background: "linear-gradient(180deg, #fff8e1 0%, #fffbeb 100%)",
                maxHeight: "min(88vh, 720px)",
              },
              body: { paddingTop: rem(12) },
            }}
          >
            <Stack gap="md">
              <Flex justify="space-between" align="flex-start" gap="sm" wrap="nowrap">
                <Text fw={700} size="sm" c="#422006" lh={1.35} style={{ minWidth: 0, flex: 1 }}>
                  {BANNER_TITLE}
                </Text>
                <CustomerOfferHighlightSaveButton {...saveState} />
              </Flex>
              {description}
              {form}
            </Stack>
          </Drawer>
        </Box>

        {/* Desktop: full inline banner */}
        <Box visibleFrom="sm" p="sm" style={BANNER_CARD_STYLES}>
          <Stack gap="xs">
            {titleRow}
            {description}
            {form}
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
